import { useEffect, useMemo, useRef } from 'react';

import type { ExternalToast } from 'sonner';
import { toast } from 'sonner';

import { usePathname } from '@polyai/common/hooks/useRouter';
import { TOAST_DURATION, Toast } from '@polyai/ui/components/atoms/Toast';
import type {
  ToastProps as Props,
  ToastType,
} from '@polyai/ui/components/atoms/Toast/types';

import { getActionName, logToDataDog } from 'lib/ddlogHelper';
import { getPageTab } from 'lib/miscHelper';
import useProjectRoute from './useProjectRoute';

export enum TOAST_LOGGER_TYPE {
  ERROR = 'errorAlertLogger',
  INFO = 'infoAlertLogger',
  WARNING = 'warningAlertLogger',
  SUCCESS = 'successAlertLogger',
}

type ToastMethodProps = Omit<Props, 'type'>;

export type Toast = ReturnType<typeof useToast>;

const TOAST_ERROR_DURATION = TOAST_DURATION * 20;

const getLoggerType = (type: ToastType) => {
  if (type === 'info') {
    return TOAST_LOGGER_TYPE.INFO;
  }
  if (type === 'success') {
    return TOAST_LOGGER_TYPE.SUCCESS;
  }
  if (type === 'warning') {
    return TOAST_LOGGER_TYPE.WARNING;
  }
  return TOAST_LOGGER_TYPE.ERROR;
};

const getAdjustedType = (type: ToastType) => {
  if (type === 'info') {
    return 'info';
  }
  if (type === 'success') {
    return 'info';
  }
  if (type === 'warning') {
    return 'warn';
  }
  return 'error';
};

const logToLogger = ({
  accountId,
  projectId,
  pathname,
  type,
  title,
  description,
}: {
  accountId: string;
  projectId: string;
  pathname: string;
} & Pick<Props, 'type' | 'title' | 'description'>) => {
  if (type === 'info') {
    return;
  }

  const page = getPageTab(pathname);
  const loggerType = getLoggerType(type);
  const adjustedType = getAdjustedType(type);

  logToDataDog(
    loggerType,
    adjustedType,
    title,
    {
      page,
      alert_message: title,
      alert_title: description,
    },
    accountId,
    projectId,
  );
};

type ShowProps = {
  accountId: string;
  projectId: string;
  pathname: string;
} & Props;

export const showToast = (props: ShowProps) => {
  const { pathname, customId, dismissable = true, ...rest } = props;
  logToLogger({
    ...props,
    pathname,
  });

  const options: ExternalToast = {
    duration: dismissable
      ? props.type !== 'error'
        ? TOAST_DURATION
        : TOAST_ERROR_DURATION
      : Infinity,
  };

  if (customId) {
    options.id = customId;
  }

  return toast.custom(
    t => (
      <Toast
        key={t}
        closeButtonProps={{
          // @ts-expect-error
          'data-dd-action': getActionName('Alert', 'close'),
        }}
        dismissable={dismissable}
        onClose={() => toast.dismiss(t)}
        {...rest}
      />
    ),
    options,
  );
};

const dismiss = (toastId: string | number) => {
  toast.dismiss(toastId);
};

export const useToast = () => {
  const pathname = usePathname();
  const { accountId, projectId } = useProjectRoute();

  const toastOptions = useRef({
    accountId,
    projectId,
    pathname,
  });

  useEffect(() => {
    toastOptions.current = {
      accountId,
      projectId,
      pathname,
    };
  }, [accountId, projectId, pathname]);

  const toast = useMemo(() => {
    const info = (props: ToastMethodProps) =>
      showToast({
        type: 'info',
        ...props,
        ...toastOptions.current,
      });

    const success = (props: ToastMethodProps) =>
      showToast({
        type: 'success',
        ...props,
        ...toastOptions.current,
      });

    const warning = (props: ToastMethodProps) =>
      showToast({
        type: 'warning',
        ...props,
        ...toastOptions.current,
      });

    const error = (props: ToastMethodProps) =>
      showToast({
        type: 'error',
        ...props,
        ...toastOptions.current,
      });

    return {
      info,
      success,
      warning,
      error,
      dismiss,
    };
  }, []);

  return toast;
};
